import { AGE_BREAKPOINTS } from '@/components/AttendanceForm/general/constants/age'
import { createNamespacedHelpers } from 'vuex'
const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')

const easiAreas = [
  {
    key: 'cabeca_pescoco',
    name: 'CABEÇA E PESCOÇO',
    factor: 0.1,
    childrenFactor: 0.2,
  },
  {
    key: 'tronco',
    name: 'TRONCO',
    factor: 0.3,
  },
  {
    key: 'membros_superiores',
    name: 'MEMBROS SUPERIORES',
    factor: 0.2,
  },
  {
    key: 'membros_inferiores',
    name: 'MEMBROS INFERIORES',
    factor: 0.4,
    childrenFactor: 0.3,
  },
]
export default {
  computed: {
    ...attendanceGettersMap(['getPhaseDataByKey', 'attendancePatient']),
    scorePhaseData() {
      return this.getPhaseDataByKey('score')
    },
    patientAge() {
      return this.$util.toAgeNumber(this.attendancePatient.birthday)
    },
    isPatientAdult() {
      return this.patientAge >= AGE_BREAKPOINTS.child
    },
    computedIga() {
      return this.scorePhaseData?.iga.value ?? '?'
    },
    computedWinrs() {
      return this.scorePhaseData?.winrs.value ?? '?'
    },
    computedPtiga() {
      return this.scorePhaseData?.ptiga.value ?? '?'
    },
    computedBsa() {
      if (!this.scorePhaseData) {
        return '?'
      }
      if (this.scorePhaseData.easi_bsa.resultado_manual) {
        return this.scorePhaseData?.easi_bsa.bsa || 0
      }

      let bsaResult = 0
      for (let areaObject of easiAreas) {
        let area = this.scorePhaseData.easi_bsa[areaObject.key]
        bsaResult += area.area * areaObject.factor
      }
      return (Math.round(100 * bsaResult) / 100).toFixed(2)
    },
    computedEasi() {
      if (!this.scorePhaseData) {
        return '?'
      }
      if (this.scorePhaseData.easi_bsa.resultado_manual) {
        return this.scorePhaseData?.easi_bsa.easi || 0
      }

      let easiResult = 0
      for (let areaObject of easiAreas) {
        let area = this.scorePhaseData.easi_bsa[areaObject.key]
        let affectedAreaWeight = this.getAffectedAreaWeight(area.area)

        if (!!area.descamacao || !!area.infiltracao) {
          let newAreaValues = {
            eritema: area.eritema,
            liquenificacao: area.liquenificacao,
          }

          if (!!area.descamacao) {
            newAreaValues.edema = area.descamacao
          }
          if (!!area.infiltracao) {
            newAreaValues.escoriacoes = area.infiltracao
          }
          area = newAreaValues
        }
        if (areaObject.childrenFactor && !this.isPatientAdult) {
          easiResult +=
            (area.eritema + area.edema + area.escoriacoes + area.liquenificacao) *
            areaObject.childrenFactor *
            affectedAreaWeight
        } else {
          easiResult +=
            (area.eritema + area.edema + area.escoriacoes + area.liquenificacao) *
            areaObject.factor *
            affectedAreaWeight
        }
      }
      return (Math.round(100 * easiResult) / 100).toFixed(2)
    },
    computedPoem() {
      if (!this.scorePhaseData) {
        return '?'
      }
      if (this.scorePhaseData.poem.resultado_manual) {
        return this.scorePhaseData?.poem.value || 0
      }
      var answers = []
      Object.keys(this.scorePhaseData.poem.answers).forEach((answer) => {
        answers.push(this.scorePhaseData.poem.answers[answer].value)
      })
      return answers.reduce((partialSum, a) => partialSum + a, 0)
    },
    computedAdct() {
      if (
        !this.scorePhaseData ||
        (Object.values(this.scorePhaseData.adct.answers).some((answer) => answer.value === null) &&
          !this.scorePhaseData.adct.resultado_manual)
      ) {
        return '?'
      }
      if (this.scorePhaseData.adct.resultado_manual) {
        return this.scorePhaseData?.adct.value || 0
      }
      var answers = []
      Object.keys(this.scorePhaseData.adct.answers).forEach((answer) => {
        answers.push(this.scorePhaseData.adct.answers[answer].value)
      })
      return answers.reduce((partialSum, a) => partialSum + a, 0)
    },
    computedScorad() {
      if (!this.scorePhaseData) {
        return '?'
      }
      const symptomKeys = ['eritema', 'edema', 'exsudacao', 'escoriacoes', 'xerose', 'liquenificacao']

      if (
        this.scorePhaseData.scorad.areas_afetadas.area_total == null ||
        symptomKeys.some(
          (key) =>
            this.scorePhaseData.scorad.niveis_sintoma[key].value == null ||
            this.scorePhaseData.scorad.coceira == null ||
            this.scorePhaseData.scorad.sono == null,
        )
      ) {
        return '?'
      }

      const affectedArea = Number(this.scorePhaseData.scorad.areas_afetadas.area_total) || 0
      const intensity = symptomKeys.reduce(
        (result, currentSymptomKey) =>
          result + (this.scorePhaseData.scorad.niveis_sintoma[currentSymptomKey].value ?? 0),
        0,
      )

      const a = affectedArea / 5
      const b = 7 * (intensity / 2)
      const c = (Number(this.scorePhaseData.scorad.coceira) || 0) + (Number(this.scorePhaseData.scorad.sono) || 0)
      return a + b + c
    },
    poemSeverity() {
      if (isNaN(this.computedPoem)) {
        return { class: 'grey--text', text: '' }
      } else if (this.computedPoem <= 2) {
        return { class: 'cyan--text', text: 'Praticamente sem lesões' }
      } else if (this.computedPoem <= 7) {
        return { class: 'green--text', text: 'Leve' }
      } else if (this.computedPoem <= 16) {
        return { class: 'orange--text', text: 'Moderado' }
      } else if (this.computedPoem <= 24) {
        return { class: 'orange--text text--darken-4', text: 'Severo' }
      } else {
        return { class: 'red--text text--darken-2', text: 'Muito severo' }
      }
    },
    adctSeverity() {
      const adct = this.scorePhaseData?.adct.answers
      if (isNaN(this.computedAdct)) {
        return { class: 'grey--text', text: '' }
      } else if (this.scorePhaseData.adct.resultado_manual && this.computedAdct <= 6) {
        return { class: 'cyan--text', text: 'Controlada' }
      } else if (this.scorePhaseData.adct.resultado_manual && this.computedAdct > 6) {
        return { class: 'orange--text', text: 'Não controlada' }
      } else if (
        this.computedAdct <= 6 &&
        adct.sintomas.value < 2 &&
        adct.coceira.value < 2 &&
        adct.eczema.value < 2 &&
        adct.sono.value < 1 &&
        adct.atividades_diarias.value < 2 &&
        adct.humor.value < 2
      ) {
        return { class: 'cyan--text', text: 'Controlada' }
      } else {
        return { class: 'orange--text', text: 'Não controlada' }
      }
    },
    computedDlqi() {
      const phaseData = this.scorePhaseData ?? this.getPhaseDataByKey('dlqi')
      return phaseData?.dlqi?.value == null ? '?' : phaseData?.dlqi?.value
    },
  },
  methods: {
    getAffectedAreaWeight(value) {
      if (value === 0) {
        return 0
      } else if (value < 10) {
        return 1
      } else if (value >= 10 && value < 30) {
        return 2
      } else if (value >= 30 && value < 50) {
        return 3
      } else if (value >= 50 && value < 70) {
        return 4
      } else if (value >= 70 && value < 90) {
        return 5
      } else {
        return 6
      }
    },
  },
}
